<script lang="ts">
	import { Stack, Text } from '@Shelly/shelly-design-system';
	import { flowEditorState } from '../../../stores/flow_editor';

	export let nodeId: string;

	$: node = $flowEditorState.nodes.find((item) => item.id === nodeId);
	$: steps = (Array.isArray(node?.data.steps) ? node?.data.steps : []) as Array<Record<string, any>>;

	function commit(next: Array<Record<string, any>>) {
		if (!node) return;
		flowEditorState.updateNode(node.id, { data: { ...node.data, steps: next } as any });
	}

	function addStep(type: 'notification' | 'setVariable' | 'wait') {
		const base = { id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, type };
		const step =
			type === 'notification'
				? { ...base, notificationType: 'event_log', message: '' }
				: type === 'setVariable'
					? { ...base, name: '', value: '' }
					: { ...base, value: 5, units: 'sec' };
		commit([...steps, step]);
	}

	function update(index: number, field: string, value: unknown) {
		commit(steps.map((step, i) => (i === index ? { ...step, [field]: value } : step)));
	}

	function remove(index: number) {
		commit(steps.filter((_, i) => i !== index));
	}
	function move(index: number, offset: number) {
		const target = index + offset;
		if (target < 0 || target >= steps.length) return;
		const next = [...steps];
		[next[index], next[target]] = [next[target], next[index]];
		commit(next);
	}
	const value = (event: Event) => (event.target as HTMLInputElement | HTMLSelectElement).value;
</script>

<Stack gap="s">
	<Text variant="body" size="m">Action Sequence</Text>
	<p class="hint">Steps run in this exact order. Use delays between actions when timing matters.</p>
	<div class="steps">
		{#each steps as step, index (step.id)}
			<div class="step-card">
				<div class="step-header">
					<strong
						>{index + 1}. {step.type === 'wait'
							? 'Delay'
							: step.type === 'setVariable'
								? 'Set variable'
								: 'Notification'}</strong
					>
					<div>
						<button title="Move up" disabled={index === 0} on:click={() => move(index, -1)}
							>↑</button
						><button
							title="Move down"
							disabled={index === steps.length - 1}
							on:click={() => move(index, 1)}>↓</button
						><button title="Remove step" on:click={() => remove(index)}>×</button>
					</div>
				</div>
				{#if step.type === 'notification'}
					<label
						>Destination<select
							value={step.notificationType || 'event_log'}
							on:change={(event) => update(index, 'notificationType', value(event))}
							><option value="event_log">Event log</option><option
								value="push_notification">Push notification</option
							><option value="mail_notification">Email</option></select
						></label
					>
					<label
						>Message<input
							type="text"
							placeholder="What should be sent?"
							value={step.message || ''}
							on:input={(event) => update(index, 'message', value(event))}
						/></label
					>
				{:else if step.type === 'setVariable'}
					<label
						>Variable name<input
							type="text"
							placeholder="e.g. mode"
							value={step.name || ''}
							on:input={(event) => update(index, 'name', value(event))}
						/></label
					>
					<label
						>Value<input
							type="text"
							value={step.value ?? ''}
							on:input={(event) => update(index, 'value', value(event))}
						/></label
					>
				{:else}
					<label
						>Duration
						<div class="duration">
							<input
								type="number"
								min="1"
								value={step.value || 1}
								on:input={(event) => update(index, 'value', Number(value(event)))}
							/><select
								value={step.units || 'sec'}
								on:change={(event) => update(index, 'units', value(event))}
								><option value="sec">Seconds</option><option value="min">Minutes</option
								></select
							>
						</div></label
					>
				{/if}
			</div>
		{/each}
	</div>
	<div class="add-row">
		<button on:click={() => addStep('notification')}><i class="fa fa-bell"></i> Notification</button
		><button on:click={() => addStep('setVariable')}
			><i class="fa fa-equals"></i> Set variable</button
		><button on:click={() => addStep('wait')}><i class="fa fa-hourglass-half"></i> Delay</button>
	</div>
</Stack>

<style>
	.hint {
		margin: 0;
		color: #64748b;
		font-size: 0.75rem;
		line-height: 1.4;
	}
	.steps {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.step-card {
		display: flex;
		flex-direction: column;
		gap: 7px;
		border: 1px solid color-mix(in srgb, #8b5cf6 28%, transparent);
		border-radius: 8px;
		padding: 9px;
		background: color-mix(in srgb, #8b5cf6 5%, transparent);
	}
	.step-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		font-size: 0.75rem;
	}
	.step-header button {
		border: 0;
		background: transparent;
		color: inherit;
		cursor: pointer;
	}
	.step-header button:disabled {
		opacity: 0.3;
	}
	label {
		display: flex;
		flex-direction: column;
		gap: 3px;
		color: #64748b;
		font-size: 0.7rem;
	}
	input,
	select {
		min-height: 30px;
		box-sizing: border-box;
		border: 1px solid rgba(148, 163, 184, 0.45);
		border-radius: 6px;
		background: var(--color-bg, transparent);
		color: inherit;
		padding: 0 7px;
	}
	.duration {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 6px;
	}
	.add-row {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}
	.add-row button {
		border: 1px solid rgba(139, 92, 246, 0.35);
		border-radius: 6px;
		background: color-mix(in srgb, #8b5cf6 8%, transparent);
		color: inherit;
		padding: 6px 8px;
		cursor: pointer;
		font-size: 0.72rem;
	}
</style>
